import { Component, EventEmitter, Input, Output } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { WeissmanModalService } from '../../../Compliance/WeissmanModalService';
import { EntityTypeIDs, EntityTypeNames, RyanInstanceId } from '../../../constants.new';
import {
    LegalDescriptionModalComponent,
    LegalDescriptionModalParams,
} from '../../../Entity/Parcel/Info/legalDescriptionModal.component';
import { TaxRateSetupModalLaunchService } from '../../../Entity/Parcel/TaxRateSetup/taxRateSetupModalLaunchService';
import {
    MessageBoxButtons,
    MessageBoxResult,
    MessageBoxService,
} from '../../../UI-Lib/Message-Box/messagebox.service.upgrade';

import * as _ from 'lodash';
import { FeatureFlagsService } from 'src/app/Common/FeatureFlags/feature-flags-service';

@Component({
    selector: 'entity-panel',
    templateUrl: './entityPanel.component.html',
    styleUrls: ['./entityPanel.component.scss'],
    standalone: false,
})
export class EntityPanelComponent {
    constructor(
        private readonly _messageBoxService: MessageBoxService,
        private readonly _weissmanModalService: WeissmanModalService,
        private readonly _taxRateSetupModalLaunchService: TaxRateSetupModalLaunchService,
        private readonly _featureFlagService: FeatureFlagsService,
    ) {}

    @Input() currentRow: any;
    @Input() entityDetails: Core.EntityTree;
    @Input() selectedEntity: any;
    @Input() revisionInfo: any;
    @Input() loadingRevisionData: boolean;
    @Input() warnOnEntityReload: boolean;
    @Input() associatedEntityMissing: boolean;

    @Output() setDirty: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() forceReloadEntity: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() loadQuickSearch: EventEmitter<void> = new EventEmitter<void>();

    EntityTypeNames = EntityTypeNames;
    ryanInstanceId = RyanInstanceId;
    addressPopoverData: any = {};
    editModeEntityPanel: boolean;

    private _cleanParcelData: any;

    get marketValueText(): string {
        return 'Total AV | MV:';
    }

    cancelChanges(): void {
        this.currentRow.intakeDocumentInfo.parcelChanges = _.cloneDeep(this._cleanParcelData);

        this._cleanParcelData = null;

        this.editModeEntityPanel = false;
    }

    isSubEntity(): boolean {
        if (!this.selectedEntity) {
            return;
        }

        return _.includes(
            [
                EntityTypeIDs.TAX_BILL,
                EntityTypeIDs.ASSESSMENT,
                EntityTypeIDs.APPEAL,
                EntityTypeIDs.FILING,
                EntityTypeIDs.PAYMENT,
            ],
            this.selectedEntity.entityTypeId,
        );
    }

    editEntityPanel(): void {
        // If parcel changes object from API, then save it off so it can be reverted to on cancel
        if (this.currentRow.intakeDocumentInfo.parcelChanges) {
            this._cleanParcelData = _.cloneDeep(this.currentRow.intakeDocumentInfo.parcelChanges);
        } else {
            // If no parcel changes, then init it with extended data.
            const parcelChangesFields = [
                'assesseeName',
                'collectorAcctNum',
                'parcelCollectorID',
                'parcelRowVersion',
                'parcelAddressRowVersion',
                'parcelCollectorRowVersion',
            ];

            this.currentRow.intakeDocumentInfo.parcelChanges = _.pick(
                this.entityDetails.extendedData,
                parcelChangesFields,
            );
            this.currentRow.intakeDocumentInfo.parcelChanges.parcelAddress = _.pick(
                this.entityDetails.extendedData.parcelAddress,
                ['address1', 'address2', 'city', 'stateID', 'zip'],
            );
            this.currentRow.intakeDocumentInfo.parcelChanges.parcelAddress.abbr =
                this.entityDetails.extendedData.parcelState.abbr;
        }

        this.setDirty.emit(true);
        this.editModeEntityPanel = true;
    }

    async showLegalDescriptionDialog(): Promise<void> {
        const params: LegalDescriptionModalParams = {
            parcel: {
                acctNum: this.entityDetails.extendedData.parcelAcctNum,
                legalDescription: this.entityDetails.extendedData.parcelLegalDescription,
            } as Weissman.Model.Parcels.Parcel,
            isEditMode: false,
        };
        await this._weissmanModalService.showAsync(
            LegalDescriptionModalComponent,
            params,
            'lg-modal super-max-modal-size',
        );
    }

    isOverridden(field: string, isAddress?: boolean): boolean {
        if (!this.currentRow.intakeDocumentInfo.parcelChanges) {
            return false;
        }

        if (field === 'taxSetup') {
            return !!this.currentRow.intakeDocumentInfo.parcelChanges.newTaxSetup;
        } else if (isAddress) {
            return !_.isEqual(
                this.currentRow.intakeDocumentInfo.parcelChanges.parcelAddress[field],
                this.entityDetails.extendedData.parcelAddress[field],
            );
        } else {
            return !_.isEqual(
                this.currentRow.intakeDocumentInfo.parcelChanges[field],
                this.entityDetails.extendedData[field],
            );
        }
    }

    async openTaxSetupModal(): Promise<void> {
        let newTaxSetup = null;
        if (this.currentRow.intakeDocumentInfo.parcelChanges) {
            newTaxSetup = this.currentRow.intakeDocumentInfo.parcelChanges.newTaxSetup;
        }

        const modalResult = await lastValueFrom(
            this._taxRateSetupModalLaunchService.openDocumentProcessingParcelTaxSetupModal(
                this.entityDetails.parcel.entityId,
                {
                    parcelCollectorID: this.entityDetails.extendedData.parcelCollectorID,
                    rowVersion: this.entityDetails.extendedData.parcelCollectorRowVersion as unknown as string,
                    collectorID: this.entityDetails.collector.entityId,
                },
                newTaxSetup,
                this.entityDetails.collector.text,
                +this.entityDetails.annualYear.text,
                !this.editModeEntityPanel,
            ),
        );

        if (modalResult) {
            this.currentRow.intakeDocumentInfo.parcelChanges.newTaxSetup = modalResult;
        }
    }

    async reloadEntity(): Promise<void> {
        if (
            this.warnOnEntityReload ||
            (this.currentRow && this.currentRow.intakeDocumentInfo && this.currentRow.intakeDocumentInfo.parcelChanges)
        ) {
            const result = await this._messageBoxService.open({
                message:
                    'Changes have been made to this entity; if you reload now, those changes will be undone. Are you sure?',
                buttons: MessageBoxButtons.OKCancel,
            });

            if (result === MessageBoxResult.OK) {
                this.setDirty.emit(true);
                this.forceReloadEntity.emit(true);
            }
        } else {
            this.forceReloadEntity.emit(true);
        }
    }
}
